import { type DependencyList, type RefObject, useEffect, useState } from "react";

import { captureMessage } from "@sentry/react";

/**
 * @description Detect whether the internal scrollbar is visible.
 * Deals with browser/OS differences and dynamic content changes.
 * Support apple dynamic scrollbar.
 *
 * @see In some rare cases it may cause an infinite loop. In that case it will stop checking and log a message to Sentry.
 */
export function useScrollbarVisible(
	bodyRef: RefObject<HTMLElement | null>,
	containerRef: RefObject<HTMLElement | null>,
	deps: DependencyList = [],
): boolean {
	const [isScrollbarVisible, setIsScrollbarVisible] = useState(false);

	useEffect(() => {
		const element = bodyRef.current;
		if (!element) {
			return;
		}

		let callCount = 0;
		let resetTimer: ReturnType<typeof setTimeout>;

		const checkScrollbar = () => {
			callCount++;
			clearTimeout(resetTimer);

			if (callCount > 10) {
				captureMessage(
					"[useScrollbarVisible] Infinite loop detected: checkScrollbar called more than 10 times in 100ms. Stopping checks.",
				);
				return;
			}

			resetTimer = setTimeout(() => {
				callCount = 0;
			}, 100);

			const tableWidth = containerRef.current?.clientWidth || 0;
			const bodyWidth = bodyRef.current?.clientWidth || 0;
			const rowWidth = bodyRef.current?.querySelector("tr")?.clientWidth || 0;
			const paddingDiff = bodyWidth - rowWidth;
			const hasScrollbar = tableWidth > rowWidth + paddingDiff;
			setIsScrollbarVisible((prev) => {
				return prev === hasScrollbar ? prev : hasScrollbar;
			});
		};

		checkScrollbar();

		const resizeObserver = new ResizeObserver(checkScrollbar);
		resizeObserver.observe(element);

		const mutationObserver = new MutationObserver(checkScrollbar);
		mutationObserver.observe(element, {
			childList: true,
			subtree: true,
		});

		return () => {
			clearTimeout(resetTimer);
			resizeObserver.disconnect();
			mutationObserver.disconnect();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [containerRef, bodyRef, ...deps]);

	return isScrollbarVisible;
}
